import React from 'react';
import PropTypes from 'prop-types';

import useFilestackPicker from '../../hooks/useFilestackPicker';

const propTypes = {

	/**
	 * Unique id for targetting the filestack picker, if any.
	 * A default will be created if not provided.
	 */
	id: PropTypes.string,

	/**
	 * The settings for filestack.
	 */
	filestack: PropTypes.shape({

		// The API key
		apiKey: PropTypes.string.isRequired,

		// The security policy and signature
		security: PropTypes.shape({

			policy: PropTypes.string,

			signature: PropTypes.string,

		}),

		// Flag indicating if mode is production.
		production: PropTypes.bool,

		// The path
		path: PropTypes.string,

		// The container
		container: PropTypes.string,

		// The region
		region: PropTypes.string,

	}).isRequired,

	/**
	 * The children content to render for the image upload component (i.e. the button to click).
	 */
	children: PropTypes.element.isRequired,

	/**
	 * Callback to handle image upload - error.
	 */
	onError: PropTypes.func,

	/**
	 * Callback to handle image upload - success.
	 */
	onSuccess: PropTypes.func.isRequired,

};

/**
 * Shared component to render an image upload handler using filestack.
 *
 * This component is used by the ImagePicker and WYSIWYGEditor components
 * to handle file uploads.
 *
 * @param {Object} props The props.
 * @returns {Object}     The rendered component.
 */
const ImageUpload = ({ id, filestack, children, onError, onSuccess }) => {

	const pickerOptions = {
		accept: 'image/*',
		maxFiles: 1,
		imageMax: [2000, 2000],
		transformations: {
			crop: true,
		},
	};

	const handleSuccess = (response) => {

		if (response.filesUploaded && response.filesUploaded.length === 1) {
			onSuccess(response.filesUploaded[0].url);
		}

	};

	const { pickerId, togglePicker } = useFilestackPicker({
		id,
		filestack,
		options: pickerOptions,
		onSuccess: handleSuccess,
		onError
	});

	if (!pickerId) { return null; }

	return (
		<div className="tc-image-upload-container">
			{React.cloneElement(children, { onClick: () => togglePicker() })}
			<div id={pickerId} />
		</div>
	);
};

ImageUpload.propTypes = propTypes;

export default ImageUpload;
